import { useCallback, useRef } from 'react';
import { useSlotScreenGeneratorStore } from '../store/slotScreenGeneratorStore';
import { useSlotAssetGeneratorStore } from '../store/slotAssetGeneratorStore';
import {
  generateComponentImages,
  generateComponentVideos,
} from '../services/slot-screen-generator';
import { saveComponentImage } from '../services/slot-screen-generator';
import { MODEL_PROVIDER_KEY_MAP } from '../../core/types/image-generation.types';
import type { ImageModelId } from '../../core/types/image-generation.types';
import { resolveApiKey } from '../utils/api-keys';
import { shouldUseProxy } from '../services/ai-proxy';

/**
 * Hook to generate images/videos for a single screen component.
 * Follows the same pattern as useSlotScreenGenerator but targets
 * per-component generation via generatorViewComponentId.
 */
export function useScreenComponentGenerator() {
  const abortRef = useRef<AbortController | null>(null);

  const isGenerating = useSlotScreenGeneratorStore((s) => {
    const comp = s.screenComponents[s.activeScreenId]?.find(
      (c) => c.id === s.generatorViewComponentId,
    );
    return comp?.status === 'generating';
  });

  /**
   * Start generating images/videos for the currently active component.
   * Validates the API key, then streams results into the store progressively.
   */
  const generate = useCallback(async () => {
    const store = useSlotScreenGeneratorStore.getState();
    const {
      activeScreenId,
      screens,
      screenComponents,
      systemPromptJson,
      generationSettings,
      generatorViewComponentId,
    } = store;

    if (!generatorViewComponentId) return;

    const components = screenComponents[activeScreenId];
    const component = components?.find((c) => c.id === generatorViewComponentId);
    if (!component) return;

    const screen = screens[activeScreenId];
    if (!screen) return;

    const prompt = component.userPrompt.trim() || component.defaultPromptHint;
    if (!prompt) {
      store.addToast({
        id: Date.now().toString(),
        type: 'info',
        message: 'Enter a prompt to generate images.',
        timestamp: Date.now(),
      });
      return;
    }

    // Validate API key before starting
    const model = (component.generationModel ?? generationSettings.model) as ImageModelId;
    const requiredProvider = MODEL_PROVIDER_KEY_MAP[model] ?? null;
    if (requiredProvider !== null && !(requiredProvider === 'google' && shouldUseProxy())) {
      const assetStore = useSlotAssetGeneratorStore.getState();
      const storeKey = assetStore.apiKeys[requiredProvider] ?? '';
      const key = resolveApiKey(requiredProvider, storeKey);
      if (!key) {
        const msg = `API key required for ${model}. Configure your ${requiredProvider} API key in the Slot Asset Generator settings.`;
        store.addToast({
          id: Date.now().toString(),
          type: 'error',
          message: msg,
          timestamp: Date.now(),
        });
        return;
      }
    }

    // Abort any existing generation
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    store.startComponentGeneration(activeScreenId, generatorViewComponentId);

    const genSettings = {
      model: component.generationModel ?? generationSettings.model,
      imageCount: component.generationImageCount ?? generationSettings.imageCount,
    };

    try {
      const generator =
        component.generationMode === 'video'
          ? generateComponentVideos(
              systemPromptJson,
              screen.name,
              screen.description,
              component,
              genSettings,
              controller.signal,
            )
          : generateComponentImages(
              systemPromptJson,
              screen.name,
              screen.description,
              component,
              genSettings,
              controller.signal,
            );

      for await (const { result } of generator) {
        // Re-read store each iteration to get fresh state (avoids stale closure)
        useSlotScreenGeneratorStore
          .getState()
          .addComponentResult(activeScreenId, generatorViewComponentId, result);

        // Save to project folder (fire-and-forget)
        if (result.status === 'ready' && (result.imageUrl || result.videoUrl)) {
          saveComponentImage(activeScreenId, generatorViewComponentId, result)
            .then((savedPath) => {
              if (savedPath) {
                useSlotScreenGeneratorStore
                  .getState()
                  .updateComponentResultSavedPath(
                    activeScreenId,
                    generatorViewComponentId,
                    result.id,
                    savedPath,
                  );
              }
            })
            .catch(() => {}); // No project root or save failed — still works in-memory
        }
      }

      useSlotScreenGeneratorStore
        .getState()
        .finishComponentGeneration(activeScreenId, generatorViewComponentId);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        // Cancellation — finishComponentGeneration already handled by cancel()
        return;
      }
      const errMsg = err instanceof Error ? err.message : String(err);
      const s = useSlotScreenGeneratorStore.getState();
      s.finishComponentGeneration(activeScreenId, generatorViewComponentId);
      s.addToast({
        id: Date.now().toString(),
        type: 'error',
        message: errMsg,
        timestamp: Date.now(),
      });
    }
  }, []);

  /**
   * Cancel an in-progress generation.
   */
  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    const store = useSlotScreenGeneratorStore.getState();
    const { activeScreenId, generatorViewComponentId } = store;
    if (generatorViewComponentId) {
      store.finishComponentGeneration(activeScreenId, generatorViewComponentId);
    }
  }, []);

  return { generate, cancel, isGenerating };
}
